import { ReactNode } from 'preact/compat';
import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { Button, ButtonLink } from '@/components/Buttons';
import { useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import MembersOnlyWarningModal from '..';
import { setMembersOnlyWarningModalState } from './actions';
import { MembersOnlyWarningModalType } from './types';

interface ContentGenerator {
  profilePicture?: () => string; // used to override the profile picture props
  title: () => ReactNode;
  content: () => ReactNode;
  secondaryLink?: () => { href: string; label: string };
  ctaText: () => string;
}

const defaultContent: ContentGenerator = {
  title: () => 'Looks like you can use more nature in your life.',
  content: () => 'Join 300,000+ animal lovers helping support ethical wildlife care worldwide.',
  ctaText: () => 'Unlock Full Access',
};

const contentGeneratorMap: Record<MembersOnlyWarningModalType | 'default', ContentGenerator> = {
  default: defaultContent,
  'guest-general': {
    profilePicture: () => 'https://zoolife.tv/assets/guest-general-locked-tiger.webp',
    title: () => 'Easy there, tiger!',
    content: () => <>Sign up to enjoy more features.</>,
    ctaText: () => 'Sign Up For Free',
    secondaryLink: () => ({ href: '/login', label: 'I Have An Account' }),
  },
  'guest-session': {
    profilePicture: () => 'https://zoolife.tv/assets/defaultLockedFeatureImage1.webp',
    title: () => 'We see you smiling. Let’s make it official!',
    content: () => <>Create a free account to unlock more live streams and stories.</>,
    ctaText: () => 'Join for Free',
  },
  'download-media': {
    ...defaultContent,
    profilePicture: () => 'https://zoolife.tv/assets/defaultLockedFeatureImage1.webp',
    secondaryLink: () => ({ href: '/habitats', label: 'Explore as a guest' }),
  },
  habitat: {
    ...defaultContent,
    secondaryLink: () => ({ href: '/habitats', label: 'Explore All Animals' }),
  },
  event: {
    ...defaultContent,
    secondaryLink: () => ({ href: '/schedule', label: 'Explore Events Calendar' }),
  },
  'ambient-mode': {
    ...defaultContent,
    content: () => (
      <>
        See the best premium livestreams.
        <br />
        On rotation with custom soundtracks.
      </>
    ),
    ctaText: () => 'Get Ambiance Mode',
  },
};

const ConnectedMembersOnlyWarningModal = () => {
  const dispatch = useDispatch();
  const logged = useSelector((state) => state.user.logged);
  const { visible, type, imageUrl, redirectOnDismiss } = useSelector((state) => state.modals.membersOnlyWarningModal);
  const onDismiss = () => dispatch(setMembersOnlyWarningModalState(null));

  useEffect(() => {
    if (visible) {
      trackMP('page-view', {
        currentPage: `MembersOnlyWarningModal.${type || 'general'}`,
        pageType: 'Modal',
      });
    }
  }, [visible, type]);

  if (!visible) return null;

  const defaultProfilePicture = 'https://zoolife.tv/assets/defaultLockedFeatureImage1.webp';

  const generatorKey = !logged ? (type === 'guest-session' ? 'guest-session' : 'guest-general') : type || 'default';
  const generator = contentGeneratorMap[generatorKey] ?? contentGeneratorMap.default;
  const profilePicture = generator.profilePicture?.() ?? imageUrl ?? defaultProfilePicture;
  const titleText = generator.title();
  const content = generator.content();
  const secondaryLink = generator.secondaryLink?.();
  const callToActionText = generator.ctaText?.();

  const dismissible = type !== 'guest-session' && !redirectOnDismiss;

  return (
    <MembersOnlyWarningModal
      profilePicture={profilePicture ?? defaultProfilePicture}
      onClickOutside={dismissible ? onDismiss : undefined}
    >
      <div>
        <MembersOnlyWarningModal.Title className="px-6">{titleText}</MembersOnlyWarningModal.Title>
        <MembersOnlyWarningModal.Content>{content}</MembersOnlyWarningModal.Content>
      </div>
      <MembersOnlyWarningModal.ButtonsContainer>
        <a
          href={logged ? '/account/info' : '/signup'}
          className="preflight preflight-a h-12 rounded-full bg-primary-green p-4 text-center text-base font-medium leading-none text-white hover:bg-secondary-green"
          onClick={onDismiss}
        >
          {callToActionText}
        </a>
        {secondaryLink && (
          <a
            href={secondaryLink.href}
            className="preflight preflight-a h-12 rounded-full border border-primary-green p-4 text-center text-base font-medium leading-none text-primary-green hover:bg-beige-light"
            onClick={onDismiss}
          >
            {secondaryLink.label}
          </a>
        )}

        <div className="p-4 text-center" aria-hidden={dismissible || undefined}>
          {redirectOnDismiss ? (
            <ButtonLink variant="link" size="lg" href="/home" onClick={onDismiss}>
              Back to home page
            </ButtonLink>
          ) : dismissible ? (
            <Button variant="link" size="lg" onClick={onDismiss}>
              Dismiss
            </Button>
          ) : null}
        </div>
      </MembersOnlyWarningModal.ButtonsContainer>
    </MembersOnlyWarningModal>
  );
};

export default ConnectedMembersOnlyWarningModal;
